import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  routingAPI,
  manifestsAPI,
  driversAPI,
  trucksAPI,
} from '../../services/api';
import RouteMap from './RouteMap';
import HammerRouteLinks from '../Common/HammerRouteLinks';

/** YYYY-MM-DD from API date strings or Date objects */
function toDateKey(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const RoutePlannerDetail = () => {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [manifests, setManifests] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [selectedManifests, setSelectedManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobPolling, setJobPolling] = useState(false);

  const loadPlan = useCallback(async () => {
    const p = await routingAPI.getPlan(id);
    setPlan(p);
    return p;
  }, [id]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const p = await routingAPI.getPlan(id);
      setPlan(p);
      const [m, dr, tr] = await Promise.all([
        manifestsAPI.getAll(),
        driversAPI.getAll(),
        trucksAPI.getAll(),
      ]);
      setManifests(m);
      setDrivers(dr);
      setTrucks(tr);
    } catch (e) {
      alert('Error loading plan: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  const planDate = plan ? toDateKey(plan.plan_date) : '';
  const manifestsForDate = manifests.filter(
    (m) => toDateKey(m.ship_date) === planDate
  );
  const onPlanManifestIds = new Set(
    (plan?.stops || []).map((s) => s.manifest_id).filter(Boolean)
  );

  const pollJob = async (jobId) => {
    setJobPolling(true);
    const poll = async () => {
      const job = await routingAPI.getJob(jobId);
      if (job.status === 'queued' || job.status === 'running') {
        setTimeout(poll, 1500);
        return;
      }
      setJobPolling(false);
      await loadPlan();
      if (job.status === 'failed') {
        alert('Optimization failed: ' + (job.error_message || 'unknown'));
      }
    };
    poll();
  };

  const handleBulkAdd = async () => {
    if (!selectedManifests.length) {
      alert('Select at least one manifest');
      return;
    }
    try {
      const result = await routingAPI.bulkAddManifests(id, selectedManifests);
      setSelectedManifests([]);
      setPlan(result.plan);
      const added = result.added?.length || 0;
      const skipped = result.skipped?.length || 0;
      if (!added) {
        const reasons = (result.skipped || [])
          .map((s) => `${s.manifest_id}: ${s.reason}`)
          .join('\n');
        alert(
          skipped
            ? `No stops were added.\n${reasons}`
            : 'No stops were added.'
        );
      } else if (skipped) {
        alert(`Added ${added} stop(s). Skipped ${skipped} (see console for details).`);
        console.warn('Skipped manifests', result.skipped);
      }
    } catch (e) {
      alert('Error adding stops: ' + e.message);
    }
  };

  const moveStop = async (index, dir) => {
    const stops = [...plan.stops].sort((a, b) => a.sequence - b.sequence);
    const j = index + dir;
    if (j < 0 || j >= stops.length) return;
    [stops[index], stops[j]] = [stops[j], stops[index]];
    try {
      const updated = await routingAPI.reorderStops(
        id,
        stops.map((s) => s.id)
      );
      setPlan(updated);
    } catch (e) {
      alert('Reorder failed: ' + e.message);
    }
  };

  const handleOptimize = async () => {
    try {
      const job = await routingAPI.optimize(id, {});
      await pollJob(job.id);
    } catch (e) {
      alert('Optimize failed: ' + e.message);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Publish this plan to manifests (stop numbers) and freight assignments?')) {
      return;
    }
    try {
      const result = await routingAPI.publish(id);
      setPlan(result.plan);
      const lines = [
        `Published.`,
        `${result.manifests_updated} manifest(s): stop order saved to Manifests (stop # column).`,
      ];
      if (result.freight_updated > 0) {
        lines.push(
          `${result.freight_updated} freight record(s): driver/truck assigned from this plan.`
        );
      } else {
        lines.push(
          '0 freight records updated (stops were added from manifests, not freight lines—or assign driver/truck on the plan for freight stops).'
        );
      }
      alert(lines.join('\n'));
    } catch (e) {
      alert('Publish failed: ' + e.message);
    }
  };

  const handleMetaUpdate = async (field, value) => {
    try {
      const updated = await routingAPI.updatePlan(id, {
        [field]: value ? parseInt(value, 10) : null,
      });
      setPlan(updated);
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  if (loading || !plan) return <div className="loading">Loading plan...</div>;

  const sortedStops = [...(plan.stops || [])].sort((a, b) => a.sequence - b.sequence);
  const canEdit = !['published', 'optimizing'].includes(plan.status);

  return (
    <div className="management-container">
      <p>
        <Link to="/route-planner">&larr; All plans</Link>
      </p>
      <div className="page-header route-plan-header">
        <div className="route-plan-header__title">
          <h1>Route plan — {planDate}</h1>
          <span className="status-badge">{plan.status}</span>
        </div>
        {canEdit && (
          <div className="route-plan-header__actions">
            <button
              type="button"
              className="btn-primary"
              disabled={jobPolling || !sortedStops.length}
              onClick={handleOptimize}
            >
              {jobPolling ? 'Optimizing…' : 'Optimize (VROOM)'}
            </button>
          </div>
        )}
      </div>

      {plan.status === 'published' && (
        <p className="route-plan-published-note">
          Published — stop order is saved on manifests and driver/truck assignments were applied where applicable.
        </p>
      )}

      {canEdit && sortedStops.length > 0 && (
        <div className="route-plan-publish-bar">
          <p>
            Draft plan with {sortedStops.length} stop{sortedStops.length === 1 ? '' : 's'}. Publish when the
            stop order is final to write stop numbers to manifests and assign driver/truck on linked freight.
          </p>
          <button type="button" className="btn-primary" onClick={handlePublish}>
            Publish plan
          </button>
        </div>
      )}

      {canEdit && sortedStops.length === 0 && (
        <p style={{ marginBottom: '1.5rem', color: '#555' }}>
          Add at least one manifest stop below before optimizing or publishing.
        </p>
      )}

      <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <label>
          Driver{' '}
          <select
            disabled={!canEdit}
            value={plan.driver_id || ''}
            onChange={(e) => handleMetaUpdate('driver_id', e.target.value)}
          >
            <option value="">—</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.first_name} {d.last_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Truck{' '}
          <select
            disabled={!canEdit}
            value={plan.truck_id || ''}
            onChange={(e) => handleMetaUpdate('truck_id', e.target.value)}
          >
            <option value="">—</option>
            {trucks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.truck_number}
              </option>
            ))}
          </select>
        </label>
        <span>
          {plan.total_distance_m != null && (
            <>
              Distance: {(plan.total_distance_m / 1609.34).toFixed(1)} mi · Duration:{' '}
              {Math.round((plan.total_duration_s || 0) / 60)} min
            </>
          )}
        </span>
      </div>

      <RouteMap depot={plan.depot} stops={sortedStops} />

      {sortedStops.length > 0 && (
        <HammerRouteLinks depot={plan.depot} stops={sortedStops} />
      )}

      {canEdit && (
        <section style={{ marginBottom: '1.5rem' }}>
          <h3>Add manifests for {planDate}</h3>
          <div style={{ maxHeight: '160px', overflow: 'auto', border: '1px solid #ddd', padding: '8px' }}>
            {manifestsForDate.length === 0 ? (
              <p>No manifests on this ship date.</p>
            ) : (
              manifestsForDate.map((m) => (
                <label key={m.id} style={{ display: 'block' }}>
                  <input
                    type="checkbox"
                    disabled={onPlanManifestIds.has(m.id)}
                    checked={selectedManifests.includes(Number(m.id))}
                    onChange={(e) => {
                      const mid = Number(m.id);
                      if (e.target.checked) {
                        setSelectedManifests((ids) =>
                          ids.includes(mid) ? ids : [...ids, mid]
                        );
                      } else {
                        setSelectedManifests((ids) => ids.filter((x) => x !== mid));
                      }
                    }}
                  />{' '}
                  {m.manifest_number} — {m.consignee_name || 'Consignee'}
                  {onPlanManifestIds.has(m.id) ? ' (on plan)' : ''}
                </label>
              ))
            )}
          </div>
          <button type="button" className="btn-secondary" onClick={handleBulkAdd}>
            Add selected to route
          </button>
        </section>
      )}

      <h3>Stops ({sortedStops.length})</h3>
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Label</th>
            <th>Address</th>
            {canEdit && <th>Reorder</th>}
          </tr>
        </thead>
        <tbody>
          {sortedStops.map((s, idx) => (
            <tr key={s.id}>
              <td>{s.sequence}</td>
              <td>{s.label}</td>
              <td style={{ maxWidth: '280px' }}>{s.address_text}</td>
              {canEdit && (
                <td>
                  <button type="button" onClick={() => moveStop(idx, -1)} disabled={idx === 0}>
                    ↑
                  </button>{' '}
                  <button
                    type="button"
                    onClick={() => moveStop(idx, 1)}
                    disabled={idx === sortedStops.length - 1}
                  >
                    ↓
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {plan.last_job && (
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#555' }}>
          Last job: {plan.last_job.status} ({plan.last_job.solver} /{' '}
          {plan.last_job.matrix_provider || 'n/a'})
          {plan.last_job.error_message ? ` — ${plan.last_job.error_message}` : ''}
        </p>
      )}
    </div>
  );
};

export default RoutePlannerDetail;
